import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import activoFinanceService from '../../../services/activoFinanceService';
import { getAreas } from '../../../services/areaService';
import { formatDateLocaleLima } from '../../../utils/dateUtils';
import Toast from '../../../components/Common/Toast';

const TABS = [
  { id: 'summary', label: 'Resumen' },
  { id: 'year', label: 'Por año' },
  { id: 'area', label: 'Por área' },
  { id: 'period', label: 'Por mes' },
];

const MONTH_SHORT = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'];

const formatMoney = (value, currency = 'PEN') => {
  const num = parseFloat(value || 0);
  const symbol = currency === 'USD' ? '$' : 'S/';
  return `${symbol} ${num.toLocaleString('es-PE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

const formatDate = (value) => (value ? formatDateLocaleLima(value) || '—' : '—');

const monthLabel = (year, month) => {
  const d = new Date(year, month - 1, 1);
  return d.toLocaleDateString('es-PE', { month: 'long', year: 'numeric' });
};

const PeriodCell = ({ row, compact = false }) => {
  if (!row?.period_label || row.period_label === '—') {
    return <span className="text-gray-400">—</span>;
  }

  if (compact) {
    const hint = row.is_retirement_period
      ? ' · baja'
      : row.is_prorated_period
      ? ' · prorrateo'
      : row.is_adjustment_period
        ? ' · ajuste'
        : row.period_status === 'not_started'
          ? ' · pendiente'
          : '';
    return (
      <span className="text-xs text-gray-800 whitespace-nowrap">
        {row.period_label}
        {hint ? <span className="text-gray-500">{hint}</span> : null}
      </span>
    );
  }

  return (
    <div className="text-xs">
      <div className="font-medium text-gray-900 whitespace-nowrap">{row.period_label}</div>
      {row.is_retirement_period ? <div className="text-red-700">Baja de activo</div> : null}
      {row.is_prorated_period ? <div className="text-teal-700">Prorrateo</div> : null}
      {row.is_adjustment_period ? <div className="text-amber-700">Ajuste</div> : null}
    </div>
  );
};

const StatChip = ({ label, value, highlight = false }) => (
  <div className={`rounded-md border px-3 py-2 ${highlight ? 'border-teal-200 bg-teal-50/50' : 'border-gray-200 bg-white'}`}>
    <p className="text-[11px] text-gray-500 leading-tight">{label}</p>
    <p className={`text-sm font-semibold leading-tight mt-0.5 ${highlight ? 'text-teal-800' : 'text-gray-900'}`}>{value}</p>
  </div>
);

const ActivosDepreciacion = () => {
  const now = new Date();
  const [tab, setTab] = useState('summary');
  const [loading, setLoading] = useState(true);
  const [areas, setAreas] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [areaFilter, setAreaFilter] = useState('');
  const [viewYear, setViewYear] = useState(String(now.getFullYear()));
  const [viewMonth, setViewMonth] = useState(String(now.getMonth() + 1));
  const [summaryRows, setSummaryRows] = useState([]);
  const [summaryTotals, setSummaryTotals] = useState(null);
  const [areaRows, setAreaRows] = useState([]);
  const [periodRows, setPeriodRows] = useState([]);
  const [periodTotal, setPeriodTotal] = useState(0);
  const [yearRows, setYearRows] = useState([]);
  const [yearTotals, setYearTotals] = useState(null);
  const [toast, setToast] = useState({ isOpen: false, message: '', type: 'info' });
  const [scheduleModal, setScheduleModal] = useState(null);
  const [scheduleLoading, setScheduleLoading] = useState(false);

  const periodParams = useMemo(
    () => ({ year: viewYear, month: viewMonth }),
    [viewYear, viewMonth],
  );

  const isYearTab = tab === 'year';

  const selectedPeriodLabel = useMemo(
    () => (isYearTab
      ? String(viewYear)
      : monthLabel(parseInt(viewYear, 10), parseInt(viewMonth, 10))),
    [viewYear, viewMonth, isYearTab],
  );

  useEffect(() => {
    getAreas({ active_only: true }).then((res) => {
      if (res.success && Array.isArray(res.data)) setAreas(res.data);
    });
  }, []);

  const loadSummary = async () => {
    setLoading(true);
    try {
      const res = await activoFinanceService.getDepreciationSummary({
        search: searchTerm || undefined,
        area_id: areaFilter || undefined,
        ...periodParams,
      });
      if (res.success) {
        setSummaryRows(res.data || []);
        setSummaryTotals(res.totals || null);
      }
    } catch {
      setToast({ isOpen: true, message: 'Error al cargar depreciación', type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const loadByArea = async () => {
    setLoading(true);
    try {
      const res = await activoFinanceService.getDepreciationByArea(periodParams);
      if (res.success) setAreaRows(res.data || []);
    } catch {
      setToast({ isOpen: true, message: 'Error al cargar por área', type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const loadByPeriod = async () => {
    setLoading(true);
    try {
      const res = await activoFinanceService.getDepreciationByPeriod(periodParams);
      if (res.success) {
        setPeriodRows(res.data || []);
        setPeriodTotal(res.total_period_amount || 0);
      }
    } catch {
      setToast({ isOpen: true, message: 'Error al cargar por período', type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const loadByYear = async () => {
    setLoading(true);
    try {
      const res = await activoFinanceService.getDepreciationByYear({
        year: viewYear,
        search: searchTerm || undefined,
        area_id: areaFilter || undefined,
      });
      if (res.success) {
        setYearRows(res.data || []);
        setYearTotals(res.totals || null);
      }
    } catch {
      setToast({ isOpen: true, message: 'Error al cargar reporte anual', type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const reloadCurrentTab = () => {
    if (tab === 'summary') loadSummary();
    if (tab === 'year') loadByYear();
    if (tab === 'area') loadByArea();
    if (tab === 'period') loadByPeriod();
  };

  useEffect(() => {
    if (tab === 'summary') loadSummary();
    if (tab === 'year') loadByYear();
    if (tab === 'area') loadByArea();
    if (tab === 'period') loadByPeriod();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);

  const openSchedule = async (activoId) => {
    setScheduleLoading(true);
    setScheduleModal({ loading: true });
    try {
      const res = await activoFinanceService.getDepreciationSchedule(activoId);
      if (res.success) setScheduleModal(res.data);
      else setScheduleModal(null);
    } catch {
      setToast({ isOpen: true, message: 'No se pudo cargar el cronograma', type: 'error' });
      setScheduleModal(null);
    } finally {
      setScheduleLoading(false);
    }
  };

  const filteredSummary = useMemo(() => summaryRows, [summaryRows]);

  return (
    <div className="p-4">
      <div className="max-w-[1600px] mx-auto space-y-3">
        <div className="flex flex-wrap items-start justify-between gap-2">
          <div>
            <nav className="text-xs text-gray-500 mb-0.5">
              <Link to="/dashboard" className="hover:text-teal-600">Dashboard</Link>
              <span className="mx-1.5">/</span>
              <Link to="/activos" className="hover:text-teal-600">Activos</Link>
              <span className="mx-1.5">/</span>
              <span className="text-gray-700">Depreciación</span>
            </nav>
            <h1 className="text-xl font-bold text-gray-800">Depreciación de activos</h1>
          </div>
          <p className="text-xs text-gray-500 max-w-md">
            Monto sin IGV prorrateado por vida útil desde el inicio de actividades.
          </p>
        </div>

        <details className="group bg-amber-50/80 border border-amber-200 rounded-md text-xs text-amber-900">
          <summary className="cursor-pointer list-none px-3 py-2 font-medium select-none [&::-webkit-details-marker]:hidden">
            Reglas de cálculo
            <span className="ml-1 text-amber-700 group-open:hidden">▸</span>
            <span className="ml-1 text-amber-700 hidden group-open:inline">▾</span>
          </summary>
          <p className="px-3 pb-2 text-amber-800/90 leading-relaxed">
            Costo depreciable = subtotal sin IGV. Cuota ref. = costo ÷ (vida útil × 12).
            Primer mes prorrateado por días; último período ajusta la diferencia.
          </p>
        </details>

        <div className="bg-white border border-gray-200 rounded-lg shadow-sm overflow-hidden">
          <div className="flex flex-wrap items-end gap-2 p-3 border-b border-gray-100 bg-gray-50/60">
            <span className="text-xs font-medium text-gray-600 pb-1.5">{isYearTab ? 'Año' : 'Período'}</span>
            <input
              type="number"
              min="2000"
              max="2100"
              value={viewYear}
              onChange={(e) => setViewYear(e.target.value)}
              className="w-20 px-2 py-1.5 text-sm border border-gray-300 rounded-md bg-white"
              title="Año"
            />
            {!isYearTab && (
            <select
              value={viewMonth}
              onChange={(e) => setViewMonth(e.target.value)}
              className="px-2 py-1.5 text-sm border border-gray-300 rounded-md bg-white min-w-[120px]"
              title="Mes"
            >
              {Array.from({ length: 12 }, (_, i) => (
                <option key={i + 1} value={String(i + 1)}>
                  {new Date(2000, i, 1).toLocaleDateString('es-PE', { month: 'short' })}
                </option>
              ))}
            </select>
            )}
            <button
              type="button"
              onClick={reloadCurrentTab}
              className="px-3 py-1.5 text-sm bg-teal-600 text-white rounded-md hover:bg-teal-700"
            >
              Actualizar
            </button>
            <span className="text-xs text-gray-500 pb-1.5 hidden sm:inline">
              {isYearTab ? (
                <>Cierre: <strong className="text-gray-700">31/12/{viewYear}</strong></>
              ) : (
                <>Cierre: <strong className="text-gray-700">{selectedPeriodLabel}</strong></>
              )}
            </span>

            {(tab === 'summary' || tab === 'year') && (
              <>
                <span className="hidden md:inline w-px h-6 bg-gray-200 mx-1 self-center" />
                <input
                  type="text"
                  placeholder="Buscar…"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && loadSummary()}
                  className="flex-1 min-w-[140px] max-w-xs px-2 py-1.5 text-sm border border-gray-300 rounded-md bg-white"
                />
                <select
                  value={areaFilter}
                  onChange={(e) => setAreaFilter(e.target.value)}
                  className="px-2 py-1.5 text-sm border border-gray-300 rounded-md bg-white"
                >
                  <option value="">Todas las áreas</option>
                  {areas.map((a) => (
                    <option key={a.id} value={a.id}>{a.name}</option>
                  ))}
                </select>
                <button
                  type="button"
                  onClick={tab === 'year' ? loadByYear : loadSummary}
                  className="px-3 py-1.5 text-sm border border-gray-300 rounded-md hover:bg-gray-50"
                >
                  Buscar
                </button>
              </>
            )}
          </div>

          <div className="flex border-b border-gray-100 px-2">
            {TABS.map((t) => (
              <button
                key={t.id}
                type="button"
                onClick={() => setTab(t.id)}
                className={`px-3 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
                  tab === t.id
                    ? 'border-teal-600 text-teal-700'
                    : 'border-transparent text-gray-500 hover:text-gray-800'
                }`}
              >
                {t.label}
              </button>
            ))}
          </div>

        {tab === 'summary' && (
          <>
            {summaryTotals && (
              <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-6 gap-2 p-3 border-b border-gray-100">
                <StatChip label="Dep. del mes" value={formatMoney(summaryTotals.period_depreciation)} highlight />
                <StatChip label="Acumulado" value={formatMoney(summaryTotals.accumulated_depreciation)} highlight />
                <StatChip label="Saldo" value={formatMoney(summaryTotals.remaining_value)} />
                <StatChip label="Costo s/IGV" value={formatMoney(summaryTotals.acquisition_base_amount)} />
                <StatChip label="Cuota anual" value={formatMoney(summaryTotals.annual_depreciation)} />
                <StatChip label="Cuota mensual ref." value={formatMoney(summaryTotals.monthly_depreciation)} />
              </div>
            )}

            <div className="overflow-x-auto">
              {loading ? (
                <div className="p-6 text-center text-sm text-gray-500">Cargando…</div>
              ) : filteredSummary.length === 0 ? (
                <div className="p-6 text-center text-sm text-gray-500">No hay activos con depreciación calculada</div>
              ) : (
                <table className="min-w-full divide-y divide-gray-200 text-xs">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-3 py-2 text-left font-medium text-gray-500">Activo</th>
                      <th className="px-3 py-2 text-left font-medium text-gray-500">Área</th>
                      <th className="px-3 py-2 text-left font-medium text-gray-500 whitespace-nowrap">Inicio · Vida</th>
                      <th className="px-3 py-2 text-left font-medium text-gray-500">Período</th>
                      <th className="px-3 py-2 text-right font-medium text-gray-500">Dep. mes</th>
                      <th className="px-3 py-2 text-right font-medium text-gray-500">Acumulado</th>
                      <th className="px-3 py-2 text-right font-medium text-gray-500">Saldo</th>
                      <th className="px-3 py-2 text-center font-medium text-gray-500 w-16">Plan</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {filteredSummary.map((row) => (
                      <tr key={row.id} className="hover:bg-gray-50/80">
                        <td className="px-3 py-2">
                          <div className="font-medium text-gray-900">{row.code}</div>
                          <div className="text-gray-700 truncate max-w-[220px]" title={row.name}>{row.name}</div>
                        </td>
                        <td className="px-3 py-2 text-gray-700">{row.area?.name || '—'}</td>
                        <td className="px-3 py-2 text-gray-600 whitespace-nowrap">
                          {formatDate(row.activity_start_date)} · {row.useful_life_years}a
                        </td>
                        <td className="px-3 py-2">
                          <PeriodCell row={row} compact />
                        </td>
                        <td className="px-3 py-2 text-right font-medium text-teal-700">
                          {formatMoney(row.period_amount, row.currency)}
                        </td>
                        <td className="px-3 py-2 text-right text-gray-700">
                          {formatMoney(row.accumulated_depreciation, row.currency)}
                        </td>
                        <td className="px-3 py-2 text-right text-gray-700">
                          {formatMoney(row.remaining_value, row.currency)}
                        </td>
                        <td className="px-3 py-2 text-center">
                          <button
                            type="button"
                            onClick={() => openSchedule(row.id)}
                            className="text-teal-700 hover:text-teal-900 font-medium"
                            disabled={scheduleLoading}
                            title="Ver cronograma"
                          >
                            Ver
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </>
        )}

        {tab === 'year' && (
          <>
            {yearTotals && (
              <div className="grid grid-cols-2 sm:grid-cols-4 xl:grid-cols-6 gap-2 p-3 border-b border-gray-100">
                <StatChip label={`Dep. ${viewYear}`} value={formatMoney(yearTotals.year_depreciation)} highlight />
                <StatChip label="Acumulado 31/12" value={formatMoney(yearTotals.accumulated_depreciation)} highlight />
                <StatChip label="Saldo" value={formatMoney(yearTotals.remaining_value)} />
                <StatChip label="Costo s/IGV" value={formatMoney(yearTotals.acquisition_base_amount)} />
                <StatChip label="Activos" value={String(yearTotals.count || 0)} />
              </div>
            )}

            <div className="overflow-x-auto">
              {loading ? (
                <div className="p-6 text-center text-sm text-gray-500">Cargando…</div>
              ) : yearRows.length === 0 ? (
                <div className="p-6 text-center text-sm text-gray-500">Sin activos para el año seleccionado</div>
              ) : (
                <table className="min-w-full divide-y divide-gray-200 text-xs">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-2 py-2 text-left font-medium text-gray-500 sticky left-0 bg-gray-50 z-10">Activo</th>
                      <th className="px-2 py-2 text-left font-medium text-gray-500">Área</th>
                      {MONTH_SHORT.map((m) => (
                        <th key={m} className="px-1.5 py-2 text-right font-medium text-gray-500 whitespace-nowrap">{m}</th>
                      ))}
                      <th className="px-2 py-2 text-right font-medium text-teal-700 whitespace-nowrap">Total año</th>
                      <th className="px-2 py-2 text-right font-medium text-gray-500 whitespace-nowrap">Acum. 31/12</th>
                      <th className="px-2 py-2 text-right font-medium text-gray-500 whitespace-nowrap">Saldo</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {yearRows.map((row) => (
                      <tr key={row.id} className="hover:bg-gray-50/80">
                        <td className="px-2 py-2 sticky left-0 bg-white z-[1]">
                          <div className="font-medium text-gray-900">{row.code}</div>
                          <div className="text-gray-600 truncate max-w-[140px]" title={row.name}>{row.name}</div>
                        </td>
                        <td className="px-2 py-2 text-gray-700">{row.area?.name || '—'}</td>
                        {MONTH_SHORT.map((_, idx) => {
                          const amount = row.monthly_amounts?.[idx + 1] ?? row.monthly_amounts?.[String(idx + 1)] ?? 0;
                          return (
                            <td key={idx} className="px-1.5 py-2 text-right text-gray-600 whitespace-nowrap">
                              {amount > 0 ? formatMoney(amount, row.currency) : '—'}
                            </td>
                          );
                        })}
                        <td className="px-2 py-2 text-right font-medium text-teal-700 whitespace-nowrap">
                          {formatMoney(row.year_amount, row.currency)}
                        </td>
                        <td className="px-2 py-2 text-right whitespace-nowrap">
                          {formatMoney(row.accumulated_depreciation, row.currency)}
                        </td>
                        <td className="px-2 py-2 text-right whitespace-nowrap">
                          {formatMoney(row.remaining_value, row.currency)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  {yearTotals?.monthly_totals && (
                    <tfoot className="bg-gray-50 font-medium">
                      <tr>
                        <td className="px-2 py-2 sticky left-0 bg-gray-50 z-10" colSpan={2}>Totales {viewYear}</td>
                        {MONTH_SHORT.map((_, idx) => {
                          const amount = yearTotals.monthly_totals[idx + 1] ?? yearTotals.monthly_totals[String(idx + 1)] ?? 0;
                          return (
                            <td key={idx} className="px-1.5 py-2 text-right text-teal-800 whitespace-nowrap">
                              {amount > 0 ? formatMoney(amount) : '—'}
                            </td>
                          );
                        })}
                        <td className="px-2 py-2 text-right text-teal-800 whitespace-nowrap">
                          {formatMoney(yearTotals.year_depreciation)}
                        </td>
                        <td className="px-2 py-2 text-right whitespace-nowrap">
                          {formatMoney(yearTotals.accumulated_depreciation)}
                        </td>
                        <td className="px-2 py-2 text-right whitespace-nowrap">
                          {formatMoney(yearTotals.remaining_value)}
                        </td>
                      </tr>
                    </tfoot>
                  )}
                </table>
              )}
            </div>
          </>
        )}

        {tab === 'area' && (
          <>
            {!loading && areaRows.length > 0 && (
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 p-3 border-b border-gray-100">
                <StatChip label="Dep. del mes" value={formatMoney(areaRows.reduce((s, r) => s + (r.period_depreciation || 0), 0))} highlight />
                <StatChip label="Acumulado" value={formatMoney(areaRows.reduce((s, r) => s + (r.accumulated_depreciation || 0), 0))} highlight />
                <StatChip label="Saldo" value={formatMoney(areaRows.reduce((s, r) => s + (r.remaining_value || 0), 0))} />
                <StatChip label="Activos" value={String(areaRows.reduce((s, r) => s + (r.count || 0), 0))} />
              </div>
            )}
            <div className="overflow-x-auto">
              {loading ? (
                <div className="p-6 text-center text-sm text-gray-500">Cargando…</div>
              ) : (
                <table className="min-w-full divide-y divide-gray-200 text-xs">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-3 py-2 text-left font-medium text-gray-500">Área</th>
                      <th className="px-3 py-2 text-right font-medium text-gray-500">Activos</th>
                      <th className="px-3 py-2 text-right font-medium text-gray-500">Dep. mes</th>
                      <th className="px-3 py-2 text-right font-medium text-gray-500">Acumulado</th>
                      <th className="px-3 py-2 text-right font-medium text-gray-500">Saldo</th>
                      <th className="px-3 py-2 text-right font-medium text-gray-500 hidden lg:table-cell">Costo s/IGV</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {areaRows.map((row) => (
                      <tr key={row.area_id ?? 'none'} className="hover:bg-gray-50/80">
                        <td className="px-3 py-2 font-medium text-gray-900">{row.area_name}</td>
                        <td className="px-3 py-2 text-right">{row.count}</td>
                        <td className="px-3 py-2 text-right font-medium text-teal-700">
                          {formatMoney(row.period_depreciation)}
                        </td>
                        <td className="px-3 py-2 text-right">{formatMoney(row.accumulated_depreciation)}</td>
                        <td className="px-3 py-2 text-right">{formatMoney(row.remaining_value)}</td>
                        <td className="px-3 py-2 text-right hidden lg:table-cell">{formatMoney(row.acquisition_base_amount)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </>
        )}

        {tab === 'period' && (
          <>
            {!loading && (
              <div className="p-3 border-b border-gray-100 max-w-xs">
                <StatChip
                  label={`Gasto · ${selectedPeriodLabel}`}
                  value={formatMoney(periodTotal)}
                  highlight
                />
              </div>
            )}
            <div className="overflow-x-auto">
              {loading ? (
                <div className="p-6 text-center text-sm text-gray-500">Cargando…</div>
              ) : periodRows.length === 0 ? (
                <div className="p-6 text-center text-sm text-gray-500">Sin depreciación en el período seleccionado</div>
              ) : (
                <table className="min-w-full divide-y divide-gray-200 text-xs">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-3 py-2 text-left font-medium text-gray-500">Activo</th>
                      <th className="px-3 py-2 text-left font-medium text-gray-500">Área</th>
                      <th className="px-3 py-2 text-left font-medium text-gray-500">Período</th>
                      <th className="px-3 py-2 text-right font-medium text-gray-500">Dep. mes</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {periodRows.map((row) => (
                      <tr key={row.id} className="hover:bg-gray-50/80">
                        <td className="px-3 py-2">
                          <div className="font-medium">{row.code}</div>
                          <div className="text-gray-600 truncate max-w-[200px]" title={row.name}>{row.name}</div>
                        </td>
                        <td className="px-3 py-2">{row.area?.name || '—'}</td>
                        <td className="px-3 py-2">
                          <PeriodCell row={row} compact />
                        </td>
                        <td className="px-3 py-2 text-right font-medium text-teal-700">
                          {formatMoney(row.period_amount, row.currency)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </>
        )}
        </div>
      </div>

      {scheduleModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-3xl max-h-[90vh] flex flex-col">
            <div className="p-6 border-b">
              <h3 className="text-lg font-semibold text-gray-900">Cronograma de depreciación</h3>
              {scheduleModal.loading ? (
                <p className="text-sm text-gray-500 mt-1">Cargando…</p>
              ) : (
                <p className="text-sm text-gray-600 mt-1">
                  {scheduleModal.activo?.code} — {scheduleModal.activo?.name}
                  {' · '}
                  {scheduleModal.finance?.useful_life_years} años ({scheduleModal.finance?.total_months} meses)
                </p>
              )}
            </div>
            {!scheduleModal.loading && scheduleModal.finance && (
              <div className="px-6 py-3 bg-gray-50 text-sm grid grid-cols-2 md:grid-cols-4 gap-2 border-b">
                <div>Costo s/IGV: <strong>{formatMoney(scheduleModal.finance.acquisition_base_amount, scheduleModal.finance.currency)}</strong></div>
                <div>Cuota mensual ref.: <strong>{formatMoney(scheduleModal.finance.monthly_depreciation, scheduleModal.finance.currency)}</strong></div>
                <div>Inicio: <strong>{formatDate(scheduleModal.activo?.activity_start_date)}</strong></div>
                <div>Fin: <strong>{formatDate(scheduleModal.finance.depreciation_end_date)}</strong></div>
                {scheduleModal.finance.first_period_amount != null &&
                  scheduleModal.finance.first_period_amount !== scheduleModal.finance.monthly_depreciation && (
                  <div className="col-span-2 md:col-span-4 text-teal-800">
                    1.er período (prorrateado):{' '}
                    <strong>{formatMoney(scheduleModal.finance.first_period_amount, scheduleModal.finance.currency)}</strong>
                  </div>
                )}
              </div>
            )}
            <div className="overflow-y-auto flex-1 p-6">
              {scheduleModal.loading ? (
                <div className="text-center text-gray-500 py-8">Cargando cronograma…</div>
              ) : (
                <table className="min-w-full text-sm">
                  <thead>
                    <tr className="text-left text-xs text-gray-500 uppercase">
                      <th className="pb-2">#</th>
                      <th className="pb-2">Mes</th>
                      <th className="pb-2 text-right">Cuota</th>
                      <th className="pb-2 text-right">Acumulado</th>
                      <th className="pb-2 text-right">Saldo</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {(scheduleModal.schedule || []).map((line) => (
                      <tr key={line.period} className={line.is_retirement ? 'bg-red-50/60' : line.is_adjustment ? 'bg-amber-50/60' : line.is_prorated ? 'bg-teal-50/40' : undefined}>
                        <td className="py-2 font-medium text-gray-700 whitespace-nowrap">
                          {line.is_adjustment
                            ? `${line.period_number}*`
                            : `${line.period_number} / ${line.total_periods}`}
                        </td>
                        <td className="py-2">
                          {monthLabel(line.year, line.month)}
                          {line.is_prorated && line.days_in_period ? (
                            <span className="block text-xs text-teal-700">
                              Prorrateo ({line.days_in_period} días)
                            </span>
                          ) : null}
                          {line.is_retirement ? (
                            <span className="block text-xs text-red-700">Baja — saldo pendiente</span>
                          ) : null}
                          {line.is_adjustment ? (
                            <span className="block text-xs text-amber-700">Ajuste por diferencia</span>
                          ) : null}
                        </td>
                        <td className="py-2 text-right">{formatMoney(line.amount, scheduleModal.finance?.currency)}</td>
                        <td className="py-2 text-right">{formatMoney(line.accumulated, scheduleModal.finance?.currency)}</td>
                        <td className="py-2 text-right">{formatMoney(line.remaining, scheduleModal.finance?.currency)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
            <div className="p-4 border-t flex justify-end">
              <button
                type="button"
                onClick={() => setScheduleModal(null)}
                className="px-4 py-2 border rounded-lg text-gray-700 hover:bg-gray-50"
              >
                Cerrar
              </button>
            </div>
          </div>
        </div>
      )}

      <Toast
        isOpen={toast.isOpen}
        message={toast.message}
        type={toast.type}
        onClose={() => setToast({ ...toast, isOpen: false })}
      />
    </div>
  );
};

export default ActivosDepreciacion;
